import { clockIn, clockOut, getPunchTokens } from '../api';

/**
 * Offline punch queue.
 *
 * When a clock in/out fails because the device has no connectivity, the punch
 * is stored here (localStorage) with its original timestamp and replayed in
 * order once the network returns. The server honors `offline_punched_at`
 * only when the punch carries a valid sync token (see refreshPunchTokens), so
 * replayed punches keep their real times; a punch without one is still
 * recorded, at the time it reaches the server, and flagged.
 *
 * Cross-tab safety assumes the Web Locks API (see withQueueLock); where it is missing
 * (jsdom, very old browsers) mutations run unserialized and only the re-read dedupe in
 * takePunchToken narrows the two-tab window.
 */

const STORAGE_KEY = 'ht_offline_punches';
const TOKENS_KEY = 'ht_punch_tokens';

// Plan 109: server-issued sync tokens. While online the app keeps a small pool;
// every punch queued offline takes one, and its nonce becomes the punch id.
export const PUNCH_TOKEN_POOL = 10;
export const PUNCH_TOKEN_REFRESH_MS = 15 * 60 * 1000;

// Plan 109 (Codex pass 2): the token pool and the queue are localStorage read-modify-write
// state shared by every tab. Mutations run inside one Web Locks critical section when the
// browser has it (Chrome / Android PWA); otherwise (jsdom, old browsers) they run directly.
const LOCK_NAME = 'hourtrax-offline-queue';
export function withQueueLock(fn) {
  try {
    if (typeof navigator !== 'undefined' && navigator.locks && typeof navigator.locks.request === 'function') {
      return Promise.resolve(navigator.locks.request(LOCK_NAME, async () => fn()));
    }
  } catch {
    /* fall through to the direct path */
  }
  return Promise.resolve().then(fn);
}

function readQueue() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeQueue(queue) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(queue));
  } catch {
    /* storage full/unavailable — punch is lost, caller already showed an error path */
  }
}

function readTokens() {
  try {
    const raw = localStorage.getItem(TOKENS_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === 'object' && Array.isArray(parsed.tokens) ? parsed : null;
  } catch {
    return null;
  }
}

function writeTokens(pool) {
  try {
    localStorage.setItem(TOKENS_KEY, JSON.stringify(pool));
  } catch {
    /* storage unavailable - queued punches fall back to a random id and replay at server time */
  }
}

/**
 * Fetch a fresh pool of sync tokens (call while online). The pool is REPLACED,
 * never topped up, so a token's issued_at is the last moment the app was online -
 * that is what bounds how far back a queued punch may be dated.
 * @returns {Promise<boolean>} true when a new pool was stored
 */
// Tokens are bound to tenant + employee + user on the server, so the stored pool carries
// the identity it was issued to and is never spent by a different login on the same device.
let currentIdentity = null;
export function setPunchIdentity(identity) {
  currentIdentity = identity && identity.userId != null
    ? { tenantId: identity.tenantId ?? null, employeeId: identity.employeeId ?? null, userId: identity.userId }
    : null;
}
function sameIdentity(a, b) {
  return !!a && !!b && String(a.tenantId) === String(b.tenantId) &&
    String(a.employeeId) === String(b.employeeId) && String(a.userId) === String(b.userId);
}

// The identity the server actually minted these tokens for (t/e/u in the signed payload).
// Read without verifying - the server verifies on replay; this only labels the pool, so a
// login switched by another tab (shared JWT in localStorage) cannot be mislabelled.
function tokenIdentity(token) {
  try {
    const part = String(token).split('.')[1];
    const b64 = part.replace(/-/g, '+').replace(/_/g, '/');
    const p = JSON.parse(atob(b64 + '='.repeat((4 - (b64.length % 4)) % 4)));
    if (p && p.u != null && p.e != null) return { tenantId: p.t ?? null, employeeId: p.e, userId: p.u };
  } catch {
    /* undecodable */
  }
  return null;
}

export async function refreshPunchTokens(employeeId) {
  if (employeeId == null) return false;
  // Capture who is asking BEFORE the network call: a login switch while it is in flight
  // must not let user A's tokens be stored for user B.
  const requester = currentIdentity;
  try {
    const res = await getPunchTokens(PUNCH_TOKEN_POOL);
    const list = Array.isArray(res?.data?.tokens) ? res.data.tokens : [];
    const tokens = list.filter(t => t && typeof t.token === 'string' && typeof t.nonce === 'string' &&
      !isNaN(Date.parse(t.expires_at)));
    // A 200 with no usable tokens (captive-portal HTML) must not wipe a good pool.
    if (tokens.length === 0) return false;
    const minted = tokenIdentity(tokens[0].token);
    if (!minted) return false;
    return await withQueueLock(() => {
      if (requester && !sameIdentity(requester, currentIdentity)) return false;
      if (!requester && currentIdentity) return false;
      if (requester && !sameIdentity(requester, minted)) return false;
      if (currentIdentity && !sameIdentity(currentIdentity, minted)) return false;
      writeTokens({ employee_id: employeeId, identity: minted, tokens, fetched_at: new Date().toISOString() });
      return true;
    });
  } catch {
    return false;
  }
}

/** Unexpired sync tokens stored for this employee. */
export function punchTokenCount(employeeId) {
  const pool = readTokens();
  if (!pool || String(pool.employee_id) !== String(employeeId)) return 0;
  return pool.tokens.filter(t => Date.parse(t.expires_at) > Date.now()).length;
}

// Take the next unexpired token for this employee (one pool per device). Belt and braces:
// the pool and queue are re-read from storage here, and a token whose nonce is already a
// queued punch id (another tab took it first) is discarded, never reused.
function takePunchToken(employeeId) {
  const pool = readTokens();
  if (!pool || String(pool.employee_id) !== String(employeeId)) return null;
  if (currentIdentity && !sameIdentity(pool.identity, currentIdentity)) {
    // Issued to another login (or before identities were stored): unusable here, and
    // keeping it would only let it be spent by the wrong account later.
    writeTokens({ ...pool, tokens: [] });
    return null;
  }
  const used = new Set(readQueue().map(i => i.id));
  const live = pool.tokens.filter(t => Date.parse(t.expires_at) > Date.now() && !used.has(t.nonce));
  const tok = live.shift() || null;
  writeTokens({ ...pool, tokens: live });
  return tok;
}

export function getQueuedPunches() {
  return readQueue();
}

// Queued items are stamped with the login that made them (same identity the token pool
// uses). Only the current login's items - plus legacy items from older builds, which carry
// no owner - are counted, shown and replayed; other logins' items stay untouched in storage
// until their owner signs in again.
function ownedByCurrent(item) {
  return !item.owner || !currentIdentity || sameIdentity(item.owner, currentIdentity);
}

export function getQueueLength() {
  return readQueue().filter(ownedByCurrent).length;
}

/**
 * Queue a punch that failed due to connectivity.
 * @param {'clock_in'|'clock_out'} type
 * @param {Object} payload - the exact request body that failed
 * @param {{queuedAt?: string, meta?: Object}} [opts] - meta is display-only
 *   (label, task title) and is never sent to the server
 * @returns the queued item (with its offline timestamp)
 */
export function queuePunch(type, payload, { queuedAt, meta } = {}) {
  return appendPunch(type, payload, { queuedAt, meta });
}

/** queuePunch inside the cross-tab critical section (use this from the UI). */
export function queuePunchExclusive(type, payload, opts = {}) {
  return withQueueLock(() => appendPunch(type, payload, opts));
}

function appendPunch(type, payload, { queuedAt, meta } = {}) {
  const tok = takePunchToken(payload?.employee_id);
  const queue = readQueue();
  const item = {
    id: tok ? tok.nonce : `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    token: tok ? tok.token : null,
    type,
    payload,
    owner: currentIdentity,
    queued_at: queuedAt || new Date().toISOString(),
    meta: meta || null,
  };
  queue.push(item);
  writeQueue(queue);
  return item;
}

/**
 * Queue a property switch made offline as the two punches the server's
 * /clockin/switch performs — clock out, then clock in — sharing one timestamp
 * so the replayed shifts meet with no gap.
 * @returns {{ at: string, items: Array }}
 */
export function queueSwitch(args) {
  return switchPunches(args, queuePunch);
}

/** queueSwitch with both items taken inside one critical section. */
export function queueSwitchExclusive(args) {
  return withQueueLock(() => switchPunches(args, queuePunch));
}

function switchPunches({ clockOutPayload, clockInPayload, meta }, queuePunch) {
  const at = new Date().toISOString();
  const out = queuePunch('clock_out', clockOutPayload, { queuedAt: at });
  const inn = queuePunch('clock_in', clockInPayload, { queuedAt: at, meta });
  return { at, items: [out, inn] };
}

/**
 * The shift the device believes is open once the queue replays: the last
 * queued item if it is a clock_in, else null. Used to rehydrate the card
 * after a reload while still offline.
 */
export function lastQueuedShift(employeeId) {
  const queue = readQueue().filter(ownedByCurrent);
  const last = queue[queue.length - 1];
  if (!last || last.type !== 'clock_in') return null;
  // The queue is per device, not per user: on a shared phone another
  // employee's queued shift must never rehydrate this one's card.
  if (employeeId != null && String(last.payload?.employee_id) !== String(employeeId)) return null;
  return last;
}

// ── Punch options cache ──────────────────────────────────────────────
// GET /clockin/punch-options is network-only, so after a reload while offline
// the Where list (and with it Clock In / Switch Property) would be empty.
// Remember the last list per employee so an offline reload stays usable.
const OPTIONS_KEY = 'ht_punch_options';

export function cachePunchOptions(employeeId, options) {
  try {
    localStorage.setItem(OPTIONS_KEY, JSON.stringify({ employee_id: employeeId, options, cached_at: new Date().toISOString() }));
  } catch {
    /* storage unavailable — the live list still rendered */
  }
}

export function cachedPunchOptions(employeeId) {
  try {
    const raw = localStorage.getItem(OPTIONS_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    if (!parsed || String(parsed.employee_id) !== String(employeeId) || !Array.isArray(parsed.options)) return null;
    return parsed.options;
  } catch {
    return null;
  }
}

// Guard against overlapping replays (mount + 'online' event can both fire).
// A second concurrent replay would re-send the same head-of-queue punch.
let replaying = false;

/**
 * Replay queued punches in order. Stops at the first connectivity failure
 * (still offline, or a 5xx / 408 / 429 from the server - the punch is kept).
 * Punches the server rejects (other 4xx - e.g. already clocked in) are dropped and reported so the UI can surface them.
 *
 * `warnings` lists punches that synced but were recorded at server time (no valid sync token).
 *
 * @returns {Promise<{replayed: number, dropped: string[], warnings: string[], stillQueued: number, skipped?: boolean}>}
 */
export async function replayQueue() {
  if (replaying) {
    return { replayed: 0, dropped: [], warnings: [], stillQueued: getQueueLength(), skipped: true };
  }
  replaying = true;
  try {
    return await doReplayQueue();
  } finally {
    replaying = false;
  }
}

// Remove one item by id against a fresh read of storage. A punch queued while
// a replay is in flight (switching right after reconnecting) lands in storage
// behind the item being sent; writing back a stale local copy would erase it.
function removeQueued(id) {
  writeQueue(readQueue().filter(i => i.id !== id));
}

function deviceTimeLabel(iso) {
  const t = new Date(iso);
  return isNaN(t.getTime()) ? String(iso) : t.toLocaleString('en-US', { month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

async function doReplayQueue() {
  let replayed = 0;
  const dropped = [];
  const warnings = [];

  for (;;) {
    const item = readQueue().find(ownedByCurrent);
    if (!item) break;
    // meta lives on the item, not in payload, so it is never sent to the server
    const body = { ...item.payload, offline_punched_at: item.queued_at, client_punch_id: item.id };
    if (item.token) body.punch_token = item.token;
    try {
      let res;
      if (item.type === 'clock_in') {
        res = await clockIn(body);
      } else if (item.type === 'clock_out') {
        res = await clockOut(body);
      } else {
        // Unknown type (older build wrote it?) — drop it rather than wedge the queue
        dropped.push(`Unknown queued punch type "${item.type}" was skipped`);
        await withQueueLock(() => removeQueued(item.id));
        continue;
      }
      const tnh = res?.data?.time_not_honoured;
      if (tnh) {
        const label = item.type === 'clock_in' ? 'Clock In' : 'Clock Out';
        warnings.push(`${label} at ${deviceTimeLabel(item.queued_at)} synced, but it was recorded at ${deviceTimeLabel(tnh.recorded_at)} - when it reached the server - because the app had no valid sync token. Your manager has been notified to correct it.`);
      }
      replayed++;
      await withQueueLock(() => removeQueued(item.id));
    } catch (err) {
      const status = err.response?.status;
      // H3-2: offline, or the server could not take it right now (5xx deploy/DB
      // error, 408, 429). The key makes a later retry safe, so keep the punch -
      // dropping it would throw worked time away with no audit row.
      if (!err.response || status >= 500 || status === 408 || status === 429) break;
      // Any other 4xx is a real refusal - drop it so the queue can't wedge, and
      // say which punch and device time it was.
      const label = item.type === 'clock_in' ? 'Clock In' : item.type === 'clock_out' ? 'Clock Out' : item.type;
      const when = deviceTimeLabel(item.queued_at);
      dropped.push(`${label} at ${when} could not be synced: ${err.response?.data?.error || 'the server rejected it'}`);
      await withQueueLock(() => removeQueued(item.id));
    }
  }

  return { replayed, dropped, warnings, stillQueued: getQueueLength() };
}
